import React, { useState, useEffect } from "react";
import { graphql } from "gatsby";
import Layout from "@herb/layouts";
import SEO from "@herb/components/seo";
import { deployUrl } from "@herb/utils";
import { Helmet } from "react-helmet";
// import { scriptCreate } from "@herb/utils/scripts";
import AdventCalendarHero from "@herb/components/AdventCalendar/AdventCalendarHero";
import AdventCalendarDoor from "@herb/components/AdventCalendar/AdventCalendarDoor";
import ReactHtmlParser from "react-html-parser";
import "./index.css";
import AdventRewardPopup from "@herb/components/AdventCalendar/AdventRewardPopup";
import AdventEmailSignup from "@herb/components/AdventCalendar/AdventEmailSignup";
import FAQAccordion from "@herb/components/FAQAccordion";
import { track } from "@herb/utils/analytics";
import { fallbackImages } from "../../utils/constants";
import DescriptionForArticlePage from "@herb/components/DescriptionForArticlePage";
import Image from "../../components/Image";

const AdventCalendar = ({ data, pageContext }) => {
  const { wordpressWpPage: page } = data;
  const { noindex } = pageContext;

  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedDoor, setSelectedDoor] = useState(null);

  const [closedDoorMessage, setClosedDoorMessage] = useState("");

  // Utility function to build URLs with UTM parameters
  const buildUrlWithUTM = (url, utmContent = "partner-link") => {
    if (!url || url === "#") return url;

    const utmParams = new URLSearchParams({
      utm_source: "herb-advent-calendar",
      utm_medium: "cta",
      utm_campaign: "advent-2025",
      utm_content: utmContent
    });

    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${utmParams.toString()}`;
  };

  const handlePartnerClick = (partnerName, linkType = "partner") => {
    track("Advent Calendar Partner Link Click", {
      partner: partnerName,
      linkType: linkType,
      utm_source: "herb-advent-calendar",
      utm_medium: "cta",
      utm_campaign: "advent-2025",
      utm_content: linkType === "powered-by" ? "powered-by" : "partner-link"
    });
  };

  const handlePopupClose = () => {
    setIsPopupOpen(false);
    document.body.style.overflow = "auto";
    setSelectedDoor(null);
    setClosedDoorMessage("");
  };

  const breadcrumbs = [
    {
      name: page.title,
      url: `/${(page || {}).slug}`
    }
  ];

  const handleDoorClick = (doorIndex, isDoorOpen) => {
    if (isDoorOpen) {
      showDoorContents(doorIndex);
    } else {
      showClosedDoorMessage();
    }
    document.body.style.overflow = "hidden";
  };

  const showDoorContents = doorIndex => {
    // show door contents
    const door = page?.contentAdventCalendar?.doors[doorIndex];
    setSelectedDoor({ ...door, doorNumber: doorIndex + 1 });
    setIsPopupOpen(true);
  };

  const showClosedDoorMessage = () => {
    // Implement the logic to show a message for closed doors
    const messages = page?.contentAdventCalendar?.lockedMessages;
    const currentMessage =
      messages && messages.length > 0
        ? messages[Math.floor(Math.random() * messages.length)].message
        : "This door is not yet open. Please check back later!";
    setClosedDoorMessage(currentMessage);
  };

  // useEffect(() => {
  //   scriptCreate();
  // }, []);

  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;
  const mobileImg =
    page?.contentAdventCalendar?.banner1?.backgroundImageMobile?.sourceUrl;

  const banner1Img =
    isMobile && mobileImg
      ? mobileImg
      : page?.contentAdventCalendar?.banner1?.backgroundImage?.sourceUrl;

  const generalContent = page?.contentAdventCalendar?.content || [];

  return (
    <div className="advent-calendar-page">
      <Layout page="advent-calendar">
        <>
          {noindex ? (
            <Helmet>
              <meta name="robots" content="noindex" />
            </Helmet>
          ) : (
            <></>
          )}
        </>
        <SEO
          title={page?.contentAdventCalendar?.seo?.title || page.title}
          description={
            page?.contentAdventCalendar?.seo?.metaDescription || page.title
          }
          shareTitle={
            page?.contentAdventCalendar?.seo?.shareTitle || page.title
          }
          url={`/${(page || {}).slug}`}
          canonical={`${deployUrl}/${(page || {}).slug}`}
          breadcrumbs={breadcrumbs}
        />

        <div className="advent-calendar-container">
          {/* Hero section */}
          <AdventCalendarHero
            titleImage={page?.contentAdventCalendar?.titleImage}
            subtitleText={page?.contentAdventCalendar?.subtitleText}
            features={page?.contentAdventCalendar?.features}
          />
          {/* Calendar main section */}
          <div id="advent-calendar-main" className="advc-calendar-main">
            <div className="advc-calendar-heading">
              {page?.contentAdventCalendar?.calendarHeading}
            </div>
            <div className="advc-doors-container">
              <div
                className="advc-banner-1"
                style={{
                  backgroundImage: `url(${banner1Img})`
                }}
              >
                <span className="advc-banner-1-text">
                  {page?.contentAdventCalendar?.banner1?.text}
                </span>
              </div>
              {page?.contentAdventCalendar?.doors?.map((door, index) => (
                <AdventCalendarDoor
                  key={index}
                  doorImage={
                    isMobile && door?.doorImageMobile
                      ? door?.doorImageMobile
                      : door?.doorImage
                  }
                  doorOpeningDate={door?.openingDate}
                  doorTitle={door?.title}
                  index={index}
                  numberPosition={door?.numberPosition}
                  onDoorClick={handleDoorClick}
                />
              ))}
            </div>
            {isPopupOpen ? (
              <AdventRewardPopup
                reward={selectedDoor?.reward}
                doorNumber={selectedDoor?.doorNumber}
                formContent={selectedDoor?.formContent}
                onClose={handlePopupClose}
              />
            ) : (
              closedDoorMessage && (
                <AdventRewardPopup
                  closedDoorMessage={closedDoorMessage}
                  onClose={handlePopupClose}
                />
              )
            )}
          </div>
          {/* Features with icon section */}
          <div className="advc-features-with-icon-text">
            {page?.contentAdventCalendar?.featuresWithIconAndText?.map(
              (item, index) => (
                <div key={index} className="advc-feature-item">
                  <img
                    className="advc-feature-icon"
                    src={item.featureIcon?.sourceUrl}
                    alt={item.featureIcon?.altText}
                  />
                  <div className="advc-feature-text">
                    <h4 className="advc-feature-title">{item.featureTitle}</h4>
                    <p className="advc-feature-description">
                      {ReactHtmlParser(item?.featureText || "")}
                    </p>
                  </div>
                </div>
              )
            )}
          </div>
          <AdventEmailSignup
            heading={page?.contentAdventCalendar?.emailSignup?.heading}
            text={page?.contentAdventCalendar?.emailSignup?.text}
            buttonLabel={page?.contentAdventCalendar?.emailSignup?.ctaLabel}
            form={page?.contentAdventCalendar?.emailSignup?.form}
            buttonLink={page?.contentAdventCalendar?.emailSignup?.ctaLink}
          />

          {/* Sponsors section */}
          <div className="advc-partners-logos">
            <p className="advc-powered-by">
              <a
                href={buildUrlWithUTM(
                  page?.contentAdventCalendar?.poweredBy?.link,
                  "powered-by"
                )}
                className="advc-powered-by-sponser"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() =>
                  handlePartnerClick(
                    page?.contentAdventCalendar?.poweredBy?.text,
                    "powered-by"
                  )
                }
              >
                {page?.contentAdventCalendar?.poweredBy?.text}
              </a>
            </p>
            <div className="advc-logos-container">
              <div className="advc-logos-top-row">
                {page?.contentAdventCalendar?.partnersLogos?.map(
                  (logoItem, index) => {
                    if (index < 2)
                      return (
                        <a
                          className="advc-partner-logo"
                          key={index}
                          href={buildUrlWithUTM(
                            logoItem.link,
                            `partner-${index + 1}`
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() =>
                            handlePartnerClick(
                              logoItem.logo?.altText || `Partner ${index + 1}`,
                              "partner"
                            )
                          }
                        >
                          <img
                            src={logoItem.logo?.sourceUrl}
                            alt={logoItem.logo?.altText}
                          />
                        </a>
                      );
                  }
                )}
              </div>
              <div className="advc-logos-middle-row second-middle-row">
                {page?.contentAdventCalendar?.partnersLogos?.map(
                  (logoItem, index) => {
                    if (index >= 2 && index < 9)
                      return (
                        <a
                          className="advc-partner-logo"
                          key={index}
                          href={buildUrlWithUTM(
                            logoItem.link,
                            `partner-${index + 1}`
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() =>
                            handlePartnerClick(
                              logoItem.logo?.altText || `Partner ${index + 1}`,
                              "partner"
                            )
                          }
                        >
                          <img
                            src={logoItem.logo?.sourceUrl}
                            alt={logoItem.logo?.altText}
                          />
                        </a>
                      );
                  }
                )}
              </div>
              <div className="advc-logos-middle-row">
                {page?.contentAdventCalendar?.partnersLogos?.map(
                  (logoItem, index) => {
                    if (index >= 9 && index < 17)
                      return (
                        <a
                          className="advc-partner-logo"
                          key={index}
                          href={buildUrlWithUTM(
                            logoItem.link,
                            `partner-${index + 1}`
                          )}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() =>
                            handlePartnerClick(
                              logoItem.logo?.altText || `Partner ${index + 1}`,
                              "partner"
                            )
                          }
                        >
                          <img
                            src={logoItem.logo?.sourceUrl}
                            alt={logoItem.logo?.altText}
                          />
                        </a>
                      );
                  }
                )}
              </div>
              {page?.contentAdventCalendar?.partnersLogos?.map(
                (logoItem, index) => {
                  if (index >= 17)
                    return (
                      <a
                        className="advc-partner-logo"
                        key={index}
                        href={buildUrlWithUTM(
                          logoItem.link,
                          `partner-${index + 1}`
                        )}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() =>
                          handlePartnerClick(
                            logoItem.logo?.altText || `Partner ${index + 1}`,
                            "partner"
                          )
                        }
                      >
                        <img
                          src={logoItem.logo?.sourceUrl}
                          alt={logoItem.logo?.altText}
                        />
                      </a>
                    );
                }
              )}
            </div>
          </div>

          {/* contents like heading, image, text etc. */}
          {generalContent && generalContent.length > 0 && (
            <div className="advc-general-content-section">
              {generalContent.map((item, index) => {
                if (
                  item.__typename ===
                  "WordpressWpPage_Contentadventcalendar_Content_Image"
                )
                  return (
                    <div
                      className="ImageForArticle"
                      key={`${item.__typename}-${index}`}
                    >
                      {item.imageLink ? (
                        <a
                          href={item.imageLink}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <Image
                            className="lazyload advc-general-content-image"
                            fallback={fallbackImages.square}
                            src={item.image?.sourceUrl}
                            alt={item.image?.altText || pageContext.name}
                            width={990}
                            height={630}
                            imgixParams={{
                              fit: "clip",
                              auto: "format,compress"
                            }}
                          />
                        </a>
                      ) : (
                        <Image
                          className="lazyload advc-general-content-image"
                          src={item.image?.sourceUrl}
                          fallback={fallbackImages.square}
                          alt={item.image?.altText || pageContext.name}
                          width={990}
                          height={630}
                          imgixParams={{
                            fit: "clip",
                            auto: "format,compress"
                          }}
                        />
                      )}
                      {item.image?.caption && (
                        <div className="articleImageCredits">
                          <div className="imageCourtesy">
                            {item.image.caption}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                else if (
                  item.__typename ===
                  "WordpressWpPage_Contentadventcalendar_Content_Text"
                )
                  return (
                    <DescriptionForArticlePage
                      item={item}
                      showSticker={false}
                      key={`${item.__typename}-${index}`}
                    />
                  );
                else if (
                  item.__typename ===
                  "WordpressWpPage_Contentadventcalendar_Content_Heading"
                )
                  return (
                    <div key={`${item.__typename}-${index}`}>
                      {item.level === "level1" ? (
                        <div className="headingWithSticker">
                          <h1 className="articlePageH1">{item.text}</h1>
                          {item?.addSticker && (
                            <div className="headingStickerContainer">
                              <Image
                                className="lazyload headingSticker"
                                src={item?.sticker420?.sourceUrl}
                                alt="Sticker"
                                imgixParams={{
                                  fit: "clip"
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ) : item.level === "level2" ? (
                        <div className="headingWithSticker">
                          <h2 className="articleSubHeadings">{item.text}</h2>
                          {item?.addSticker && (
                            <div className="headingStickerContainer">
                              <Image
                                className="lazyload headingSticker"
                                src={item?.sticker420?.sourceUrl}
                                alt="Sticker"
                                imgixParams={{
                                  fit: "clip"
                                }}
                              />
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="headingWithSticker">
                          <h3 className="articleSubHeadingsH3">{item.text}</h3>
                          {item?.addSticker && (
                            <div className="headingStickerContainer">
                              <Image
                                className="lazyload headingSticker"
                                src={item?.sticker420?.sourceUrl}
                                alt="Sticker"
                                imgixParams={{
                                  fit: "clip"
                                }}
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                else if (
                  item.__typename ===
                  "WordpressWpPage_Contentadventcalendar_Content_Faq"
                )
                  return (
                    <div
                      className="articles-faq"
                      key={`${item.__typename}-${index}`}
                    >
                      <h2 className="articleSubHeadings faqSectionHeading">
                        Frequently Asked Questions
                      </h2>
                      {item?.faq?.map((faqItem, faqIndex) => (
                        <FAQAccordion
                          key={faqIndex}
                          question={faqItem.question}
                          answer={faqItem.answer}
                        />
                      ))}
                    </div>
                  );
              })}
            </div>
          )}

          {/* FAQ section */}
          {/* {page?.contentAdventCalendar?.faq &&
            page.contentAdventCalendar.faq.length > 0 && (
              <div className="advc-faq-section">
                <h2 className="advc-faq-heading">Frequently Asked Questions</h2>
                <div className="advc-faq-list">
                  {page.contentAdventCalendar.faq.map((faqItem, index) => (
                    <FAQAccordion
                      key={index}
                      id={`advent-faq-${index}`}
                      question={faqItem.question}
                      answer={faqItem.answer}
                    />
                  ))}
                </div>
              </div>
            )} */}
        </div>
      </Layout>
    </div>
  );
};

export default AdventCalendar;

export const pageQuery = graphql`
  query($id: String!) {
    wordpressWpPage(id: { eq: $id }) {
      title
      slug
      contentAdventCalendar {
        calendarHeading
        subtitleText
        calendarHeading
        poweredBy {
          text
          link
        }
        emailSignup {
          ctaLabel
          ctaLink
          form
          heading
          text
        }
        titleImage {
          altText
          sourceUrl
        }
        subtitleText
        features {
          features
        }
        featuresWithIconAndText {
          featureTitle
          featureText
          featureIcon {
            altText
            sourceUrl
          }
        }
        partnersLogos {
          logo {
            altText
            sourceUrl
          }
          link
        }
        banner1 {
          text
          backgroundImage {
            altText
            sourceUrl
          }
          backgroundImageMobile {
            altText
            sourceUrl
          }
        }
        doors {
          title
          openingDate
          numberPosition
          doorImage {
            altText
            sourceUrl
          }
          doorImageMobile {
            altText
            sourceUrl
          }
          reward {
            backgroundImage {
              altText
              sourceUrl
            }
            heading {
              color
              backgroundColor
              text
            }
            title {
              color
              backgroundColor
              text
            }
            product {
              ... on WordpressWpProduct {
                id
                title
                images
              }
            }
            discount {
              code
              percentage
            }
            text {
              backgroundColor
              color
              text
            }
            ctaLabel
          }
          formContent {
            ctaLabel
            formFooterText
            formHeading
            formSubheading
            formImage {
              altText
              sourceUrl
            }
            promoUrl
          }
        }
        lockedMessages {
          message
        }
        seo {
          metaDescription
          shareTitle
          title
        }
        content {
          ... on WordpressWpPage_Contentadventcalendar_Content_Heading {
            __typename
            fieldGroupName
            text
            level
            sticker420 {
              sourceUrl
            }
            addSticker
          }
          ... on WordpressWpPage_Contentadventcalendar_Content_Image {
            __typename
            fieldGroupName
            imageLink
            image {
              sourceUrl
              altText
            }
          }
          ... on WordpressWpPage_Contentadventcalendar_Content_Text {
            __typename
            fieldGroupName
            text
            addSticker
            sticker420 {
              sourceUrl
            }
          }
          ... on WordpressWpPage_Contentadventcalendar_Content_Faq {
            __typename
            fieldGroupName
            faq {
              answer
              question
            }
          }
        }
      }
    }
  }
`;
